<template>
  <b-card no-body class="reports-design-accordion-card" key="config-label">
    <div class="reports-design-accordion-header" v-b-toggle.reports-config-label>
      <AppIcon name="barcode" />Etiqueta
      <AppIcon name="chevron-down" class="reports-design-accordion-chevron" />
    </div>
    <b-collapse id="reports-config-label" accordion="reports-config-format" :visible="startOpen">
      <b-card-body>
        <b-alert show variant="info" class="small">
          Definí <b>cuántas columnas y filas</b> de etiquetas entran en la hoja.
          El resto de las medidas se ajusta dentro de ese espacio: cada campo
          muestra su máximo. La medida de hoja y la orientación se toman de la
          solapa <b>PDF</b>.
        </b-alert>

        <div v-if="!pageSizesLoaded" class="text-center text-muted py-4">
          <b-spinner small class="mr-2" />Cargando medidas de hoja…
        </div>

        <b-alert v-else-if="!sheetMm" show variant="warning" class="small">
          La medida de hoja «{{ page }}» no está en la lista.
          Elegí una en la solapa <b>PDF</b>.
        </b-alert>

        <template v-else>
          <b-alert :show="true"
                   :variant="budget.overX || budget.overY ? 'danger' : 'secondary'"
                   class="small d-flex align-items-center">
            <AppIcon :name="budget.overX || budget.overY ? 'exclamation-triangle' : 'grid'" class="mr-2" />
            <span v-if="budget.overX || budget.overY">
              La grilla no entra en la hoja de {{ sheetMm.w }} × {{ sheetMm.h }} mm.
              Reducí el tamaño de etiqueta, la separación o los márgenes.
            </span>
            <span v-else>
              <b>{{ budget.cols }} × {{ budget.rows }} = {{ budget.perPage }}</b>
              etiquetas por hoja ({{ sheetMm.w }} × {{ sheetMm.h }} mm).
              Espacio libre: {{ budget.freeX }} mm horizontal · {{ budget.freeY }} mm vertical.
            </span>
          </b-alert>

          <b-row>
            <b-col cols="12">
              <p class="reports-config-label__section">Grilla de la hoja</p>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Columnas por hoja">
                <b-form-input type="number" size="sm" min="1" step="1" v-model.number="form.label_cols"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Filas por hoja">
                <b-form-input type="number" size="sm" min="1" step="1" v-model.number="form.label_rows"></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <p class="reports-config-label__section">Tamaño de la etiqueta</p>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Ancho (mm)" :description="'máx ' + budget.maxWidth + ' mm'">
                <b-form-input type="number" size="sm" step="0.1" min="0" :max="budget.maxWidth"
                              :state="form.label_width_mm > budget.maxWidth ? false : null"
                              v-model.number="form.label_width_mm"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Alto (mm)" :description="'máx ' + budget.maxHeight + ' mm'">
                <b-form-input type="number" size="sm" step="0.1" min="0" :max="budget.maxHeight"
                              :state="form.label_height_mm > budget.maxHeight ? false : null"
                              v-model.number="form.label_height_mm"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Padding interno (mm)" description="Aire entre el borde y el contenido">
                <b-form-input type="number" size="sm" step="0.1" min="0" v-model.number="form.label_padding_mm"></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <p class="reports-config-label__section">Separación entre etiquetas</p>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Horizontal (mm)"
                            :description="budget.maxGutterX === null ? 'Sin límite (1 columna)' : 'máx ' + budget.maxGutterX + ' mm'">
                <b-form-input type="number" size="sm" step="0.1" min="0" :max="budget.maxGutterX"
                              :state="budget.maxGutterX !== null && form.label_gutter_x_mm > budget.maxGutterX ? false : null"
                              v-model.number="form.label_gutter_x_mm"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Vertical (mm)"
                            :description="budget.maxGutterY === null ? 'Sin límite (1 fila)' : 'máx ' + budget.maxGutterY + ' mm'">
                <b-form-input type="number" size="sm" step="0.1" min="0" :max="budget.maxGutterY"
                              :state="budget.maxGutterY !== null && form.label_gutter_y_mm > budget.maxGutterY ? false : null"
                              v-model.number="form.label_gutter_y_mm"></b-form-input>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <p class="reports-config-label__section">Márgenes de la hoja</p>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Superior (mm)" :description="'máx ' + budget.maxMarginTop + ' mm'">
                <b-form-input type="number" size="sm" step="0.1" min="0" :max="budget.maxMarginTop"
                              :state="form.label_margin_top_mm > budget.maxMarginTop ? false : null"
                              v-model.number="form.label_margin_top_mm"></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="3" cols="6">
              <b-form-group label="Izquierdo (mm)" :description="'máx ' + budget.maxMarginLeft + ' mm'">
                <b-form-input type="number" size="sm" step="0.1" min="0" :max="budget.maxMarginLeft"
                              :state="form.label_margin_left_mm > budget.maxMarginLeft ? false : null"
                              v-model.number="form.label_margin_left_mm"></b-form-input>
              </b-form-group>
            </b-col>

            <b-col lg="12" class="mt-3">
              <b-button variant="dark" class="pull-right" size="sm"
                        :disabled="budget.overX || budget.overY"
                        @click="save">
                <AppIcon name="save" class="mr-1" />Guardar Etiqueta
              </b-button>
            </b-col>
          </b-row>
        </template>
      </b-card-body>
    </b-collapse>
  </b-card>
</template>

<script>
  import AppIcon from '@/components/inc/icon/AppIcon'
  import Error from '@/handler/error'
  import serviceAPI from '../../services'

  // Columnas m_reports.label_* que este componente edita.
  const FIELDS = [
    'label_cols', 'label_rows',
    'label_width_mm', 'label_height_mm',
    'label_gutter_x_mm', 'label_gutter_y_mm',
    'label_margin_top_mm', 'label_margin_left_mm',
    'label_padding_mm',
  ]

  export default {
    name: 'ReportLabelLayout',
    components: {
      AppIcon,
    },
    props: {
      // Registro del reporte (m_reports) — de acá salen los valores label_*.
      report: {
        type: Object,
        default: () => ({}),
      },
      // Medida de hoja y orientación actuales (se editan en la solapa PDF).
      page: {
        type: String,
        default: 'a4',
      },
      orientation: {
        type: String,
        default: 'portrait',
      },
      // Medidas de hoja del backend (las carga el padre una sola vez).
      pageSizesMm: {
        type: Object,
        default: () => ({}),
      },
      pageSizesLoaded: {
        type: Boolean,
        default: false,
      },
      startOpen: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        form: FIELDS.reduce((acc, key) => { acc[key] = null; return acc }, {}),
      }
    },
    created() {
      this.seedForm()
    },
    watch: {
      'report.id'() {
        this.seedForm()
      },
    },
    computed: {
      // Medida de la hoja en mm ya resuelta la orientación. null si la API no
      // respondió o si el código de hoja no está en la lista.
      sheetMm() {
        if(!this.pageSizesLoaded) {
          return null
        }
        const size = this.pageSizesMm[String(this.page || '').toLowerCase()]
        if(!size) {
          return null
        }
        let { w, h } = size
        if(String(this.orientation).toLowerCase() === 'landscape') {
          [w, h] = [h, w]
        }
        return { w, h }
      },
      // Con columnas/filas fijas: cuánto puede medir como máximo cada etiqueta /
      // separación / margen sin que la grilla se pase de la hoja. `ready:false`
      // mientras no se sabe la medida de hoja.
      budget() {
        const f = this.form
        const round = value => Math.round((Number(value) || 0) * 100) / 100
        const clampMax = value => Math.max(0, round(value))

        const cols = Math.max(1, parseInt(f.label_cols) || 1)
        const rows = Math.max(1, parseInt(f.label_rows) || 1)
        const page = this.sheetMm

        if(!page) {
          return {
            ready: false,
            cols, rows, perPage: cols * rows,
            freeX: null, freeY: null, overX: false, overY: false,
            maxWidth: null, maxHeight: null, maxGutterX: null, maxGutterY: null,
            maxMarginLeft: null, maxMarginTop: null,
          }
        }

        const w = Number(f.label_width_mm) || 0
        const h = Number(f.label_height_mm) || 0
        const gx = Number(f.label_gutter_x_mm) || 0
        const gy = Number(f.label_gutter_y_mm) || 0
        const ml = Number(f.label_margin_left_mm) || 0
        const mt = Number(f.label_margin_top_mm) || 0

        const usedX = ml + cols * w + (cols - 1) * gx
        const usedY = mt + rows * h + (rows - 1) * gy

        return {
          ready: true,
          cols, rows, perPage: cols * rows,
          freeX: round(page.w - usedX),
          freeY: round(page.h - usedY),
          overX: usedX > page.w + 0.01,
          overY: usedY > page.h + 0.01,
          maxWidth: clampMax((page.w - ml - (cols - 1) * gx) / cols),
          maxHeight: clampMax((page.h - mt - (rows - 1) * gy) / rows),
          maxGutterX: cols > 1 ? clampMax((page.w - ml - cols * w) / (cols - 1)) : null,
          maxGutterY: rows > 1 ? clampMax((page.h - mt - rows * h) / (rows - 1)) : null,
          maxMarginLeft: clampMax(page.w - cols * w - (cols - 1) * gx),
          maxMarginTop: clampMax(page.h - rows * h - (rows - 1) * gy),
        }
      },
    },
    methods: {
      // Copia los label_* del registro del reporte al form. Sin defaults del
      // lado del front: el origen es m_reports.label_* (migración + ReportsCommand).
      seedForm() {
        const num = value => {
          if(value === null || value === undefined || value === '') {
            return null
          }
          const parsed = Number(value)
          return Number.isFinite(parsed) ? parsed : null
        }
        FIELDS.forEach(key => {
          this.form[key] = num(this.report ? this.report[key] : null)
        })
      },
      save() {
        if(!this.report || !this.report.id) {
          this.$awn.alert('No se seleccionó ningun reporte')
          return
        }
        if(!this.budget.ready) {
          this.$awn.alert('Todavía no se cargaron las medidas de hoja. Esperá unos segundos y reintentá.')
          return
        }
        if(this.budget.overX || this.budget.overY) {
          this.$awn.alert('La grilla de etiquetas no entra en la hoja. Ajustá el tamaño, la separación o los márgenes.')
          return
        }

        const payload = { id: this.report.id, format: 'label' }
        FIELDS.forEach(key => {
          if(this.form[key] !== null && this.form[key] !== '') {
            payload[key] = this.form[key]
          }
        })

        const loader = this.$loading.show()
        serviceAPI.editarConfiguration(payload)
          .then(() => {
            loader.hide()
            this.$awn.success('Datos guardados con éxito')
            this.$emit('saved')
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
    },
  }
</script>

<style scoped>
  .reports-config-label__section {
    color: var(--app-color-text-muted, #6c757d);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin: 6px 0 2px;
    text-transform: uppercase;
  }
  .reports-config-label__section:first-child {
    margin-top: 0;
  }
</style>
